import React, {
  CSSProperties,
  FocusEvent,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Dropdown,
  DropdownOnSearchChangeData,
  DropdownProps,
  Icon,
  InputProps,
} from "semantic-ui-react";

// Interface
import { SearchTypes } from "./SearchBoxI";

import "react-lib/css/DropdownOptions.scss";

export type SearchBoxDropdownProps<T = undefined> = ToDisplayProps<T> & {
  onEvent: OnEventHandler;
  // data
  id?: number | string;
  style?: CSSProperties;
  boxStyle?: CSSProperties;
  clearable?: boolean;
  defaultText?: number | string;
  disabled?: boolean;
  dropdownStyle?: CSSProperties;
  error?: boolean;
  fluid?: boolean;
  icon?: InputProps["icon"];
  iconStyle?: CSSProperties;
  label?: string;
  noResultsMessage?: ReactElement;
  placeholder?: string;
  search?: boolean;
  searchedItemList?: Record<string, any>[];
  selectedItem?: T extends object ? T : Exclude<SelectedItem, object>;
  size?: string;
  startSearchLength?: number;
  text?: string;
  type?: SearchTypes;
  valueKey?: string;
  verbose?: boolean;
  vertical?: boolean;

  inline?: boolean;
  limit?: number;
  loading?: boolean;
  role?: string;
  upward?: boolean;
  useWithKey?: boolean;
  searchedItemListWithKey?: Record<string, any>;
  showAllOption?: boolean;

  mapOptions?: (items: any[]) => Record<string, any>[];
  setSelectedItem?: SelectedItemHandler;
  onAdditionalUrlParams?: (searchQuery?: string) => Record<string, any>;
  onBlur?: (e: FocusEvent<HTMLElement>, data: DropdownProps) => void;
  onBlurInput?: (e: FocusEvent<HTMLElement>, data: DropdownProps) => void;
  onClear?: () => void;
  onFocus?: () => void;
};

export type SelectedItemHandler = (
  value: any,
  key: any,
  data: Record<string, any> | null,
  action?: string,
) => void;

export type OnEventHandler = (e: {
  message: any;
  params: Pick<SearchBoxDropdownProps, "id" | "limit" | "role" | "type" | "verbose"> & {
    action?: string;
    searchText?: string;
    urlParams?: Record<string, any>;
  };
}) => void;

type SelectedItem = Record<string, any> | number | string | null;

type ToDisplayProps<T> = T extends Record<string, any> | null
  ? { toDisplay: ToDisplay }
  : { toDisplay?: ToDisplay };

type ToDisplay = (item: Record<string, any>) => number | string;

const SearchBoxDropdown = (props: SearchBoxDropdownProps) => {
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Ref
  const isFocusRef = useRef<boolean>(false);
  const isClearRef = useRef<boolean>(false);
  const dropdownRef = useRef<any>(null);
  const timeoutRef = useRef<any>(0);

  const handleClear = useCallback(() => {
    props.onEvent({
      message: "ItemSearch",
      params: {
        action: "clear",
        id: props.id,
        role: props.role,
        type: props.type,
      },
    });
  }, [props.id, props.role, props.type]);

  // Callback Effect
  const handleOnClick = useCallback(async (ev: MouseEvent) => {
    const target = ev.target as HTMLElement;
    const parent = target.closest(".dropdown");

    if (isFocusRef.current && !parent) {
      const element = dropdownRef.current?.ref.current?.firstElementChild as HTMLElement | null;

      if (element) {
        element.blur();
      }
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleOnClick);

    return () => {
      document.removeEventListener("click", handleOnClick);
    };
  }, []);

  useEffect(() => {
    if (props.selectedItem === null) {
      handleClear();
    }
  }, [props.selectedItem]);

  useEffect(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, [props.searchedItemList, props.searchedItemListWithKey]);

  const value = useMemo(() => {
    if (props.selectedItem) {
      return typeof props.selectedItem === "object"
        ? // * ถ้า selectedItem เป็น object ต้อง มี toDisplay เท่านั้นเพื่อ return value กลับมา
          props.toDisplay?.(props.selectedItem)
        : props.selectedItem;
    }

    return "";
  }, [props.selectedItem, props.toDisplay, searchText]);

  const mapOptions = useCallback(
    (items: any[]) =>
      items.filter(Boolean).map((item) => {
        const content = item.name_code || item.full_name_code || item.full_name || item.name;
        const text = `${item.code} ${item.name} ${item.name_code} ${item.full_name} ${item.first_name} ${item.last_name} ${item.first_name_en} ${item.last_name_en} ${item.full_name_code}`;

        return {
          key: item[props.valueKey || "id"],
          text: value ? content : text,
          value: item[props.valueKey || "id"],
          content,
        };
      }),
    [props.valueKey, value],
  );

  const searchedList = useMemo(() => {
    const key = `${props.type}_${props.id}`;
    const itemList = props.searchedItemList;
    const listWithKey = props.searchedItemListWithKey?.[key];

    const sourceList = props.useWithKey ? listWithKey : itemList;

    if (sourceList) {
      return (sourceList.items || sourceList) as Record<string, any>[] | undefined;
    }

    return [];
  }, [
    props.id,
    props.searchedItemList,
    props.searchedItemListWithKey,
    props.type,
    props.useWithKey,
  ]);

  const options = useMemo(() => {
    if (searchedList) {
      return props.mapOptions ? props.mapOptions(searchedList) : mapOptions(searchedList);
    }

    return [];
  }, [mapOptions, props.selectedItem, searchedList]);

  const open = useMemo(() => {
    const key = `${props.type}_${props.id}`;
    const itemList = props.useWithKey
      ? props.searchedItemListWithKey?.[key]
      : props.searchedItemList;

    if (itemList) {
      const items = itemList.items || itemList;
      const hasItems = items.length > 0;
      const isNoSelectedItem = props.selectedItem === null;

      return hasItems && isNoSelectedItem && isFocusRef.current && !isLoading;
    }

    return false;
  }, [
    isLoading,
    props.id,
    props.searchedItemList,
    props.searchedItemListWithKey,
    props.selectedItem,
    props.type,
    props.useWithKey,
  ]);

  const noResultsMessage = useMemo(
    () => (isLoading ? null : props.noResultsMessage),
    [isLoading, props.noResultsMessage],
  );

  const showDefaultText = !searchText && !!props.defaultText && options.length === 0;

  const dropdownOptions = showDefaultText
    ? [{ key: 1, text: props.defaultText, value: props.defaultText }]
    : options;

  // console.log('SearchBoxDropdown showDefaultText: ', showDefaultText);
  // console.log('SearchBoxDropdown options: ', options);
  // console.log('SearchBoxDropdown props.defaultText: ', props.defaultText);
  // console.log('SearchBoxDropdown searchText: ', searchText);

  useEffect(() => {
    if (options.length > 0 && value === null && !isFocusRef.current) {
      console.log("open", open);

      handleClear();
    }
  }, [open, options, value]);

  const handleSearchChange = (
    event: SyntheticEvent<HTMLElement> | null,
    { searchQuery }: DropdownOnSearchChangeData,
  ) => {
    if (searchQuery.length <= 2 && isLoading) {
      setIsLoading(false);
    }

    setSearchText(searchQuery);

    if (searchText.length === 0 && searchQuery.length === 1 && value) {
      props.setSelectedItem?.(null, null, null);
    }

    clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      clearTimeout(timeoutRef.current);

      handleSearch(searchQuery);
    }, 250);
  };

  const handleChange = (event: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    if (data.value === "" && props.clearable) {
      props.onClear?.();
    }

    if (data.value === "" || event.type === "blur") {
      if (!props.onBlur) {
        props.setSelectedItem?.("", null, null, isClearRef.current ? "clear" : "");
      }

      if (options.length > 0) {
        handleClear();
      }

      return;
    }

    const object = data.options?.find((item) => data.value === item.value);

    if (!object) {
      return;
    }

    const selectedItem = (searchedList ?? []).find(
      (item) => item[props.valueKey || "id"] === data.value,
    );

    props.setSelectedItem?.(data.value, object.key, selectedItem ?? null);

    setSearchText("");
  };

  const handleClick = (event: SyntheticEvent) => {
    const target = event.target as HTMLElement;

    if (event.type === "click" && target.className.includes("clear")) {
      const inputElm = target.parentElement?.firstElementChild as HTMLInputElement | null;

      if (inputElm) {
        isClearRef.current = true;

        inputElm.focus();
        inputElm.blur();
      }
    }
  };

  const handleBlur = async (e: FocusEvent<HTMLElement>, data: DropdownProps) => {
    const target = e.target as HTMLInputElement;

    if (props.selectedItem && !isClearRef.current) {
      props.onBlurInput?.(e, data);
    }

    if (!props.selectedItem || isClearRef.current) {
      props.onBlur?.(e, {
        ...data,
        value: isClearRef.current ? "" : target.value,
      });
    }

    setTimeout(() => {
      isFocusRef.current = false;

      setSearchText("");

      isClearRef.current = false;
    });
  };

  const handleFocus = () => {
    isFocusRef.current = true;

    props.onFocus?.();

    if (!value && !isClearRef.current && props.defaultText) {
      handleSearchChange(null, { searchQuery: props.defaultText.toString() });
    }
  };

  const handleSearch = (searchQuery: string) => {
    if (searchQuery.length >= (props.startSearchLength ?? 3)) {
      setIsLoading(true);

      const urlParams = props.onAdditionalUrlParams?.(searchQuery);

      props.onEvent({
        message: "ItemSearch",
        params: {
          id: props.id,
          limit: props.limit,
          role: props.role,
          searchText: searchQuery,
          type: props.type,
          urlParams,
          verbose: props.verbose,
        },
      });

      return;
    }

    if (options.length > 0) {
      handleClear();
    }
  };

  return (
    <div
      id={`SearchBoxDropdown-${props.type}`}
      className={props.inline ? "inline-label" : ""}
      style={{
        ...(props.vertical
          ? {
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
            }
          : {
              alignItems: "center",
              display: "flex",
              justifyContent: "flex-start",
            }),
        ...props.boxStyle,
      }}
    >
      <div style={{ marginRight: props.label ? "10px" : "0px" }}>{props.label}</div>
      <div style={{ position: "relative", ...props.style }}>
        <Dropdown
          ref={dropdownRef}
          id={`SearchBoxDropdown-Dropdown-${props.id}`}
          className={Number(props.size) ? `size ${props.size}` : ""}
          style={props.dropdownStyle}
          clearable={props.clearable ?? true}
          disabled={props.disabled}
          error={props.error}
          fluid={props.fluid}
          loading={props.loading ?? isLoading}
          noResultsMessage={noResultsMessage}
          placeholder={props.placeholder}
          search={
            props.search && props.showAllOption
              ? (options: any[]) => options
              : (props.search ?? true)
          }
          searchQuery={searchText}
          text={props.text}
          upward={props.upward}
          value={showDefaultText ? props.defaultText : value}
          selection
          options={dropdownOptions}
          onBlur={handleBlur}
          onChange={handleChange}
          onClick={handleClick}
          onFocus={handleFocus}
          onSearchChange={handleSearchChange}
          icon={
            props.icon ? (
              <Icon
                style={{
                  marginRight: "-1rem",
                  marginTop: "-1em",
                  ...props.iconStyle,
                }}
                name={props.icon}
              />
            ) : (
              "dropdown"
            )
          }
          open={
            // !!props.noResultsMessage &&
            searchText.length >= (props.startSearchLength ?? 3) && !open ? true : open
          }
        />
      </div>
    </div>
  );
};

SearchBoxDropdown.displayName = "SearchBoxDropdown";

export default React.memo(SearchBoxDropdown) as <T extends SelectedItem | undefined = undefined>(
  props: SearchBoxDropdownProps<T>,
) => ReactElement;
